/**
 * Audit log query (03-modules.md "Platform modules": query by target, actor, date;
 * 05-api.md "Admin": `GET /v1/admin/audit?target=`). A tenant-scoped view for
 * owners and managers with `audit.view` (decided after M1 task 4).
 */
import { z } from 'zod';
import { isoTimestampSchema, pageQuerySchema } from './common.js';
import { actorKindSchema } from './events.js';
import { uuidSchema } from './ids.js';

const TARGET = /^[a-z][a-z0-9_]*(:[0-9a-f-]{36})?$/;

export const auditQuerySchema = pageQuerySchema.extend({
  /** `order` (every order) or `order:<uuid>` (one order). */
  target: z
    .string()
    .regex(TARGET, { message: 'Expected a target type, optionally with :<uuid>' })
    .optional(),
  /** The staff user (or other actor id) who made the change, or approved it by override. */
  actor: uuidSchema.optional(),
  outletId: uuidSchema.optional(),
  /** Inclusive lower bound on occurredAt. */
  from: isoTimestampSchema.optional(),
  /** Exclusive upper bound on occurredAt. */
  to: isoTimestampSchema.optional(),
});
export type AuditQuery = z.output<typeof auditQuerySchema>;

export const auditEntrySchema = z.object({
  id: uuidSchema,
  occurredAt: isoTimestampSchema,
  outletId: uuidSchema.nullable(),
  actorKind: actorKindSchema,
  actorId: uuidSchema.nullable(),
  /** The manager who approved an override. */
  onBehalfOf: uuidSchema.nullable(),
  terminalId: uuidSchema.nullable(),
  ip: z.string().nullable(),
  requestId: z.string().nullable(),
  command: z.string(),
  targetType: z.string(),
  targetId: uuidSchema.nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  reason: z.string().nullable(),
});
